import { HttpClient } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Address } from '@fantaz/shared/data-access/models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  Observable,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  of,
  switchMap,
} from 'rxjs';
import { AddressSuggestion, SmartyAutocompleteResponse } from './types';

@UntilDestroy()
@Component({
  selector: 'fz-smarty-autocomplete',
  styleUrls: ['./smarty-autocomplete.component.scss'],
  templateUrl: './smarty-autocomplete.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class SmartyAutocompleteComponent implements OnInit {
  /**
   * The API key to use for the Google Maps Places API.
   * Defaults to a test key.
   */
  @Input()
  apiKey?: string | null | undefined;

  /**
   * The address to display in the autocomplete field.
   */
  @Input()
  address = '';

  /**
   * Event emitted when the address in the autocomplete field changes.
   */
  @Output()
  addressChange: EventEmitter<Address> = new EventEmitter<Address>();

  /**
   * The reference to the autocomplete field.
   */
  formControl = new FormControl<string>('');

  suggestions$: Observable<AddressSuggestion[]> = of([]);

  constructor(private readonly httpClient: HttpClient) {}

  ngOnInit() {
    if (!this.apiKey) {
      throw new Error('API key is required');
    }
    //
    this.suggestions$ = this.formControl.valueChanges.pipe(
      untilDestroyed(this),
      debounceTime(500),
      distinctUntilChanged((a, b) => a === b),
      filter((value) => {
        if (value) {
          return value.trim().split(/\s+/).length >= 3;
        }
        return false;
      }),
      switchMap((address) => {
        // prepare options
        const options = {
          search: address as string,
          selected: '',
        };

        // call the service
        return this.autocompleteAddress(options);
      }),
    );
  }

  /**
   * Function to build the address string displayed in the autocomplete options
   *
   * @param address Address - the address to display
   * @param suffix boolean - whether to display the suffix or not
   * @returns string - the address string formatted for display
   */
  private generateDisplayName(address: Address, suffix?: boolean): string {
    let r = `${address.addressLine1} `;
    if (address.secondary) {
      r += `${address.secondary} `;
      if (address.entries && address.entries > 1) {
        r += `(${address.entries}${suffix ? ' entries' : ''})`;
      }
    }
    return `${r} ${address.city}, ${address.state} ${address.zipCode}`;
  }

  /**
   * Function to call the SmartyStreets API to get the autocomplete suggestions
   *
   * @param options object - the options to pass to the API
   * @param options.search string - the search string
   * @param options.selected string - the selected address (optional)
   * @returns Observable<AddressSuggestion[]> - the list of suggestions
   */
  autocompleteAddress(options: any): Observable<AddressSuggestion[]> {
    const url = new URL(
      'https://us-autocomplete-pro.api.smartystreets.com/lookup',
    );
    url.searchParams.append('key', this.apiKey as string);
    url.searchParams.append('search', options.search);

    if (options.selected) {
      url.searchParams.append('selected', options.selected);
    }

    return this.httpClient.get<SmartyAutocompleteResponse>(url.toString()).pipe(
      map((res: SmartyAutocompleteResponse) => {
        return res.suggestions.map((address) => {
          const addr = new Address({
            addressLine1: address.street_line,
            city: address.city,
            state: address.state,
            country: 'US',
            zipCode: address.zipcode,
            entries: address.entries,
            secondary: address.secondary,
          });
          return {
            label: this.generateDisplayName(addr, true),
            value: addr,
            source: 'smarty',
          } as AddressSuggestion;
        });
      }),
      switchMap((suggestions) => {
        const index = suggestions.findIndex((s) =>
          s.value?.entries && s?.value?.entries > 1 ? true : false,
        );

        if (index > -1) {
          console.log(
            'AddressServiceSmartyImpl -> autocompleteAddress -> sub-address',
          );
          const first = suggestions[index];
          return this.autocompleteAddress({
            search: first.value?.addressLine1 as string,
            selected: this.generateDisplayName(first.value as Address, false),
          }).pipe(
            map((result) => {
              // remove element at index
              const list = suggestions.splice(index, 1);
              return result.concat(list);
            }),
          );
        }

        return of(suggestions);
      }),
      catchError(() => {
        return [];
      }),
    );
  }

  onChoseSuggestion(option: any) {
    if (option.value) {
      this.addressChange.emit(option.value);
    }
  }
}
